import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import {
  getAllEligibleEmployees,
  getRenewalClasses,
  getRenewalPlanPeriod,
  savePerksEmployeesToRenewalClasses,
} from 'core'
import { RenewalPerksEmployeeRequest } from 'core/application/benefit/benefitRenewal/perks/savePerksEmployeesToRenewalClasses'

import { Alert, Button, Card, Col, Divider, Icon, Notification, Row, Skeleton, Tooltip } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import EligibleEmployeesPerksRenewalTable from './EligibleEmployeesPerksRenewalTable'

const AssignPerksEmployeeClasses = () => {
  const { t: translate } = useTranslation()
  const history = useHistory()

  const { data: renewalClasses } = useQueryRequestV2({
    initialState: null,
    queryFn: () => getRenewalClasses(),
  })
  const { data: eligibleEmployees } = useQueryRequestV2({
    initialState: null,
    queryFn: () => getAllEligibleEmployees(),
  })

  const { data: renewalPlanPeriod } = useQueryRequestV2({ initialState: null, queryFn: () => getRenewalPlanPeriod() })

  const { sendCommand: sendPerksEmployeesWithRenewalClasses, isLoading: isRenewalClassesSubmitLoading } =
    useCommandRequestV2((values: RenewalPerksEmployeeRequest[]) => savePerksEmployeesToRenewalClasses(values))

  const [submitList, setSubmitList] = useState<RenewalPerksEmployeeRequest[]>([])

  const renewal = parseZoneFormat(renewalPlanPeriod?.startDate) + ' - ' + parseZoneFormat(renewalPlanPeriod?.endDate)

  const unAssignedEmployees = submitList?.filter((x) => Number(x.classId) === -1)
  const unAssignedEmployeesCount = unAssignedEmployees.length

  useEffect(() => {
    if (eligibleEmployees) {
      const temp: RenewalPerksEmployeeRequest[] = []
      eligibleEmployees.forEach((employee) => {
        temp.push({ id: employee.employeeId, classId: employee.renewalClass?.id || (employee.ineligible ? null : -1) })
      })
      setSubmitList(temp)
    }
  }, [eligibleEmployees])

  const handleSubmit = async () => {
    const result = await sendPerksEmployeesWithRenewalClasses(submitList)
    if (result.completed) {
      Notification.success('Employees have been assigned to classes successfully!')
      history.push(ROUTE_PATHS.RENEWAL_TASKS)
    }
  }

  const submitButton = (
    <Button
      type="primary"
      disabled={unAssignedEmployeesCount > 0}
      loading={isRenewalClassesSubmitLoading}
      onClick={handleSubmit}
    >
      Submit
    </Button>
  )

  return (
    <Card title="Assign employees to classes">
      <Skeleton loading={!renewalClasses || !eligibleEmployees}>
        <Row>
          <Col sm={24} lg={17}>
            <h5 className="mb-16">Confirm your employee benefit class assignments</h5>
            <p className="color-gray-8 mb-16">
              You need to ensure that all of your benefit-eligible employees have a WorkPerks class assignment for the
              upcoming plan period.
            </p>
            <p className="color-gray-8 mb-16">
              Employees will default to their current class assignment for the upcoming plan period unless that class is
              no longer offered. In that case, the employee is marked as “Unassigned” and will need to be assigned to an
              eligible class.
            </p>
            <p className="color-gray-8 mb-16">
              If an employee is no longer eligible for the benefit, they can be terminated as a user from your Manage
              People page or marked as an “Ineligible” user in the Class assignment column.
            </p>
          </Col>
        </Row>
        {renewalClasses && eligibleEmployees && (
          <Card title="Eligible employees">
            <EligibleEmployeesPerksRenewalTable
              renewalClasses={renewalClasses}
              employees={eligibleEmployees}
              translate={translate}
              submitList={submitList}
              onSelectedItemsChanged={(items) => setSubmitList(items)}
            />
          </Card>
        )}
        <Divider className="mb-16 mt-32 -mx-24 w-auto" />
        <div className="flex flex__end">
          {unAssignedEmployeesCount > 0 ? (
            <Tooltip
              title="Unassigned employees!"
              overlayInnerStyle={{ background: 'transparent', padding: 0 }}
              overlay={
                <Card title="Unassigned employees!">
                  You still have {unAssignedEmployeesCount} {unAssignedEmployeesCount > 1 ? 'employees' : 'employee'}{' '}
                  not assigned to a {renewal} class!
                </Card>
              }
            >
              {submitButton}
            </Tooltip>
          ) : (
            submitButton
          )}
        </div>
      </Skeleton>
    </Card>
  )
}

export default AssignPerksEmployeeClasses
